import React, { useContext, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Download, Sync } from '@mui/icons-material';
import { MaterialReactTable, MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, type MRT_ColumnDef, type MRT_RowSelectionState } from 'material-react-table';
import { MRT_Localization_JA } from 'material-react-table/locales/ja';

import Log from '@/Logger';

import { FeatureContext } from '../../FeatureContext';

import { ItemNameCell, multiSelectionFilter } from '../util/ItemSerlectDoropDown/ItemNameFIlter'
import InsightReporter from '../util/insightReporter/insightReporter';
import { splitObjectKey } from '../util/splitObjectKey';
import { handleClickDownload, handleClickDownloadAll } from './Common.hooks';

import { useS3FileList } from '@/hooks/useS3FileList';
import { useDeviceDisplayName } from '@/hooks/useDeviceDisplayName';

type Props = {
    path: string
}

type ButtonStates = {
    [id: string]: boolean;
}

type CsvRecord = {
    name: string
    serialId: string
    deviceDisplayName: string
    lastModified: string
    size?: number
    download?: any
}

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

// CSV出力のカラム比率
const DOCUMENT_NAME_WIDTH_RATIO = 0.75;
const DEVICE_NAME_WIDTH_RATIO = 0.07;
const LAST_MODIFIED_WIDTH_RATIO = 0.07;
const SIZE_WIDTH_RATIO = 0.05;
const OTHER_WIDTH_RATIO = 0.05;

// デフォルト幅の定数
const DEFAULT_DEVICE_NAME_WIDTH = 130;

function getPositiveNumber(val: any): number {
    if (!Number.isFinite(val)) return 0;
    if (val <= 0) return 0;

    return val;
}

function formatSize(originalSize?: number) {
    const size = getPositiveNumber(originalSize);
    const tooltip = `${size.toLocaleString(undefined, { maximumFractionDigits: 2 })} バイト`;

    let view: number;
    let unit: string;

    if (size === 0) {
        view = 0;
        unit = 'KB';
    } else if (size < KB) {
        view = 1;
        unit = 'KB';
    } else if (size < MB) {
        view = size / KB;
        unit = 'KB';
    } else if (size < GB) {
        view = size / MB;
        unit = 'MB';
    } else {
        view = size / GB;
        unit = 'GB';
    }

    return <Tooltip title={tooltip} arrow><span>{`${view.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unit}`}</span></Tooltip>;
}

const CsvList = (props: Props) => {
    const features = useContext(FeatureContext);
    const logger = Log.getLogger();

    const tableRef = React.useRef<HTMLDivElement>(null);

    // カスタムフックを使用してPDFデータを管理
    const { list, isFetching: isCsvRecordsFetching, isLoading: isCsvRecordsLoading, refetch: refetchCsvList } = useS3FileList('CsvRecord', props.path);
    // デバイス表示名を管理するカスタムフック
    const { getDisplayName, isLoading: isDeviceDisplayNameLoading } = useDeviceDisplayName();

    // 読み込み中状態を定義
    const isLoading = list === undefined || !tableRef.current || isCsvRecordsLoading || isDeviceDisplayNameLoading;

    // デバイス表示名を付与したデータを生成
    const data = useMemo<CsvRecord[]>(() => {
        return list?.map((record) => ({
            ...record,
            deviceDisplayName: getDisplayName(record.serialId),
        })) ?? [];
    }, [list, getDisplayName]);

    //リロード連打防止用のステート
    const [isButtonActive, setIsButtonActive] = React.useState(true);
    //ダウンロード連打防止用ステート
    const [buttonStates, setButtonStates] = React.useState<ButtonStates>({});

    const [selectedDeviceList, setSelectedDeviceList] = React.useState<string[]>([]);

    const handleClickDebounce = (name: any, key: string, id: string) => {
        //IDをもとにステートを更新
        setButtonStates((prevButtonStates) => ({
            ...prevButtonStates,
            [id]: true,
        }));

        handleClickWithDebounce(name, key, id);
    };

    const handleClickWithDebounce = async (name: any, key: string, id: any) => {
        try {
            switch (key) {
                case 'download':
                    await handleClickDownload(name);
                    break;
                case 'downloadAll':
                    await handleClickDownloadAll(name);
                    break;
            }
        } catch (error) {
            logger.ui.info('CsvList', 'handleClickWithDebounce', `Error:${JSON.stringify(error)}`);
        } finally {
            setButtonStates((prevButtonStates) => ({
                ...prevButtonStates,
                [id]: false,
            }));
        }
    };
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

    const columns: MRT_ColumnDef<CsvRecord>[] = [
        {
            accessorKey: 'name',
            header: 'ファイル名',
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DOCUMENT_NAME_WIDTH_RATIO,
            Cell: ({ cell }) => (
                splitObjectKey(cell.getValue<string>()).documentName
            ),
            sortingFn: (a, b) => {
                const aName = splitObjectKey(a.original.name).documentName
                const bName = splitObjectKey(b.original.name).documentName
                if (aName === bName) return 0;
                if (aName < bName) return -1;
                return 1;
            },
            filterFn: 'includesString'
        },
        {
            accessorKey: 'deviceDisplayName',
            header: 'デバイス名',
            minSize: 130,
            maxSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DEVICE_NAME_WIDTH_RATIO,
            Cell: ({ cell }) => (
                <ItemNameCell
                    itemName={cell.getValue<string>()}
                    width={tableRef.current?.clientWidth && tableRef.current?.clientWidth * DEVICE_NAME_WIDTH_RATIO}
                />
            ),
            Filter: ({ column }) => {
                const deviceNames = Array.from(new Set<string>(
                    data.map((record) => record.deviceDisplayName)
                ));
                const width = tableRef.current?.clientWidth ? tableRef.current.clientWidth * DEVICE_NAME_WIDTH_RATIO : DEFAULT_DEVICE_NAME_WIDTH;
                return multiSelectionFilter<CsvRecord>(
                    column,
                    deviceNames,
                    selectedDeviceList,
                    setSelectedDeviceList,
                    width
                );
            },
            filterFn: (row, _columnIds, filterValue) => {
                return filterValue.includes(row.getValue('deviceDisplayName'));
            },
        },
        {
            accessorKey: 'lastModified',
            header: '更新日時',
            minSize: 148,
            maxSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * LAST_MODIFIED_WIDTH_RATIO,
        },
        {
            accessorKey: 'size',
            header: 'サイズ',
            muiTableHeadCellProps: {
                align: 'right',
            },
            muiTableBodyCellProps: {
                align: 'right',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * SIZE_WIDTH_RATIO,
            Cell: ({ cell }) => formatSize(cell.getValue<number>()),
            sortingFn: (a, b) => (a.original.size ?? 0) - (b.original.size ?? 0),
        },
        {
            accessorKey: 'download',
            header: 'ダウンロード',
            enableColumnActions: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                align: 'center',
            },
            muiTableBodyCellProps: {
                align: 'center',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * OTHER_WIDTH_RATIO,
            Cell: ({ row }) => (
                <IconButton edge='end' onClick={(event) => {
                    logger.ui.info('CsvList', 'download');
                    //クリック時に押下したボタンにIDを付与
                    event.currentTarget.id = "download" + row.id
                    const id = event.currentTarget.id as string;
                    handleClickDebounce(row.original.name, 'download', id)
                }} size='small' disabled={buttonStates["download" + row.id]}>
                    <Download fontSize='small' />
                </IconButton>
            ),
            enableColumnFilter: false,
        },
    ];

    return (
        <>
            <MaterialReactTable
                columns={columns}
                data={data}
                positionPagination={'top'}
                enablePagination={false}
                enableFullScreenToggle={false}
                enableStickyHeader
                enableDensityToggle={false}
                enableGlobalFilterModes
                enableRowSelection
                getRowId={(row) => row.name}
                onRowSelectionChange={setRowSelection}
                muiTableBodyRowProps={({ row }) => ({
                    onClick: row.getToggleSelectedHandler(),
                    sx: { cursor: 'pointer' },
                })}
                state={{ rowSelection, isLoading }}
                muiLinearProgressProps={{
                    color: 'primary',
                    sx: { height: '3px' },
                }}
                initialState={{
                    density: 'compact',
                    sorting: [{
                        id: 'lastModified',
                        desc: true
                    }],
                    showGlobalFilter: true,
                    showColumnFilters: true,
                }}
                muiTableContainerProps={{ ref: tableRef, sx: { height: 'calc(100vh - 189px)', width: 'calc(100vw - 57px)' }, style: { overflowX: 'auto', overflowY: 'scroll' } }}
                muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                        width: '100%',
                        height: '100%',
                    },
                }}
                muiTableBodyCellProps={{ style: { whiteSpace: 'normal' } }} // ファイル名が長い場合に横スクロールではなく改行で対応する
                enableBottomToolbar={false}
                localization={MRT_Localization_JA}
                renderToolbarInternalActions={({ table }) => (
                    <Box>
                        <Tooltip title="選択されたファイルをダウンロード" arrow>
                            <span>
                                <IconButton
                                    name='downloadAll'
                                    edge='end'
                                    size='medium'
                                    style={{ marginRight: '0px' }}
                                    disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()} //選択時以外ボタンを無効に
                                    onClick={(event) => {
                                        const fileNamesSelected = Object.keys(rowSelection)
                                        logger.ui.info('PdfList', 'downloadAll');
                                        //クリック時に押下したボタンにIDを付与
                                        handleClickDebounce(fileNamesSelected, 'downloadAll', "downloadAll")
                                    }}
                                >
                                    <Download />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="再読み込み" arrow>
                            <IconButton
                                edge='end'
                                onClick={() => {
                                    if (isButtonActive && !isCsvRecordsFetching) { // ローディング中は無効化
                                        logger.ui.info('CsvList', 'reflesh');
                                        setRowSelection({});
                                        setIsButtonActive(false);
                                        refetchCsvList();
                                        // refetchは非同期なので、連打防止のため少し待ってから有効化
                                        setTimeout(() => setIsButtonActive(true), 1000);
                                    }

                                }}
                                size='medium'
                                style={{ marginRight: '0px' }}
                                disabled={!isButtonActive || isCsvRecordsFetching}
                            >
                                {isCsvRecordsFetching ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <Sync fontSize='medium' />
                                )}
                            </IconButton>
                        </Tooltip>
                        {features.insightReporter() && (
                            <InsightReporter
                                CsvRecords={
                                    data.filter(item => Object.keys(rowSelection).includes(item.name))
                                        .slice()
                                        .sort((a, b) => {
                                            const aName = a.name.substring(a.name.lastIndexOf('/') + 1);
                                            const bName = b.name.substring(b.name.lastIndexOf('/') + 1);
                                            return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
                                        })
                                        .map(item => ({
                                            ...item,
                                            name: `s3://${import.meta.env.VITE_AWS_BUCKET_NAME}/${item.name}`
                                        }))
                                }
                            />
                        )}
                        <MRT_ToggleFiltersButton table={table} />
                        <MRT_ShowHideColumnsButton table={table} />
                    </Box>
                )}
            />
        </>
    );
};

export default CsvList;
